import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { api } from '../api/endpoints';
import { LoadingPill } from '../components/LoadingPill';
import { NonBuildingBadge } from '../components/NonBuildingBadge';

function PermitDetailPage() {
  const { permitId, sourceId } = Route.useSearch();
  const graph = useQuery({
    queryKey: ['permit-graph', sourceId, permitId],
    queryFn: () => api.permitGraph(permitId ?? '', sourceId ?? ''),
    enabled: Boolean(permitId && sourceId),
  });

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Permit detail
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Internal: one permit with its linked planning cases and contractors. The jurisdiction table
        now lives on Coverage.
      </Typography>
      {permitId && !sourceId && (
        <Typography color="error" sx={{ mb: 2 }}>
          This permit link is incomplete. Open the permit from search results to include its source.
        </Typography>
      )}
      {permitId && sourceId && (
        <Paper sx={{ p: 2, mb: 2 }}>
          {graph.isPending && <LoadingPill label="Loading linked permit…" />}
          {graph.isError && <Typography color="error">Permit detail failed.</Typography>}
          {graph.data && (
            <>
              <Typography variant="h6">
                {graph.data.permit.source_permit_number ?? graph.data.permit.permit_id}
                <NonBuildingBadge permit={graph.data.permit} />
              </Typography>
              <Typography>
                {graph.data.permit.description ?? graph.data.permit.permit_type ?? 'Permit'}
              </Typography>
              <Typography variant="body2">
                {graph.data.permit.address_line1 ?? 'No published address'} · APN{' '}
                {graph.data.permit.parcel_apn ?? '—'}
              </Typography>
              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                Related planning cases ({graph.data.planning.length})
              </Typography>
              <List dense>
                {graph.data.planning.map((x) => (
                  <ListItem
                    key={x.link_id}
                    secondaryAction={
                      <Button href={`/planning-detail?caseId=${encodeURIComponent(x.case_id)}`}>
                        Planning
                      </Button>
                    }
                  >
                    <ListItemText
                      primary={x.source_case_number ?? x.case_id}
                      secondary={`${x.match_method} · ${(x.match_confidence * 100).toFixed(0)}% · ${x.is_ambiguous || x.candidate_count > 1 ? 'ambiguous candidate' : 'unambiguous'}`}
                    />
                  </ListItem>
                ))}
              </List>
              <Typography variant="subtitle1">
                Related contractors ({graph.data.contractors.length})
              </Typography>
              <List dense>
                {graph.data.contractors.map((x) => (
                  <ListItem
                    key={x.link_id}
                    secondaryAction={
                      x.credential_id || x.entity_id ? (
                        <Button
                          href={`/contractor-detail?kind=${x.credential_id ? 'credential' : 'entity'}&id=${encodeURIComponent(x.credential_id ?? x.entity_id ?? '')}`}
                        >
                          Contractor
                        </Button>
                      ) : undefined
                    }
                  >
                    <ListItemText
                      primary={x.credential_id ?? x.entity_id ?? 'Unresolved contractor'}
                      secondary={`${x.match_method} · ${(x.match_confidence * 100).toFixed(0)}% · ${x.is_ambiguous || x.candidate_count > 1 ? 'ambiguous candidate' : 'unambiguous'}`}
                    />
                  </ListItem>
                ))}
              </List>
              <Chip size="small" label={`canonical permit ID ${graph.data.permit.permit_id}`} />
            </>
          )}
        </Paper>
      )}
    </Box>
  );
}

export const Route = createFileRoute('/permit-detail')({
  validateSearch: z.object({ permitId: z.string().optional(), sourceId: z.string().optional() }),
  component: PermitDetailPage,
});
